import { DebugKey, canUseDOM, createDebug, tryParse } from '@ps/web-core-utils';
import type { Client } from '@stomp/stompjs';
import { Frame, over } from '@stomp/stompjs';
import { merge, repeat } from 'lodash';
import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';
import { shareReplay, startWith } from 'rxjs/operators';
import SockJS from 'sockjs-client';

import { ConnectionState } from './enums/ConnectionState';
import type { StompHeaders as TypedHeaders } from './types';

export type { Frame } from '@stomp/stompjs';

export interface APIConfig {
  getHeaders(): object | undefined;
  getReconnect(currentDelay: number, disconnectReason: string): number;
  requestDestination: string;
  requestTimeout: number;
  responseChannel: string;
  sockJSOptions?: SockJS.Options;
  url: string;
}

export interface Connection {
  config: APIConfig;
  client?: Client;
  state: ConnectionState;
  disconnectReason?: string;
}

declare module '@stomp/stompjs' {
  export interface Client {
    headers?: TypedHeaders;
    send(destination: string, headers?: TypedHeaders, body?: string): unknown;
    subscriptions?: Record<string, unknown>;
  }
}

const subjects: { [key: string]: Observable<Connection> } = {};

const socketDebug = createDebug(DebugKey.Socket);
const frameDebug = createDebug(DebugKey.Frame);

const debug = (message: string) => {
  const prefix = message.substring(0, 4);
  const { frames } = Frame.unmarshall(message.substring(4));

  if (!frames.length) {
    socketDebug(message);
    return;
  }

  frames.forEach((frame: Frame) => {
    const body = tryParse(frame.body);
    // eslint-disable-next-line no-control-regex
    const head = prefix + Frame.marshall(frame.command, frame.headers).replace(/\n\n\x00$/, '');

    if (body && canUseDOM) {
      frameDebug('%s%o', head, body);
    } else if (body) {
      const split = head.split('\n');
      split.pop();
      frameDebug(`${repeat('%s\n', Object.keys(split).length)}%o`, ...split, body);
    } else {
      frameDebug(head);
    }
  });
};

const connectClient = (config: APIConfig, client: Client, observer: Observer<Connection>) => {
  if (client.connected) {
    return;
  }

  observer.next({ state: ConnectionState.CONNECTING, client, config });
  let connectionTimeout: NodeJS.Timeout;
  client.connect(
    { ...config.getHeaders() },
    (frame?: Frame) => {
      connectionTimeout = setTimeout(() => (client.reconnect_delay = 0), 5000);
      observer.next({ state: ConnectionState.CONNECTED, client, config });
    },
    (error: string) => null,
    ({ reason: disconnectReason }: { reason: string }) => {
      clearTimeout(connectionTimeout);
      client.reconnect_delay = config.getReconnect(client.reconnect_delay, disconnectReason);
      client.headers = merge(client.headers, config.getHeaders());
      observer.next({ config, state: ConnectionState.DISCONNECTED, client, disconnectReason });
      socketDebug('Lost connection from server: %s', disconnectReason);
    }
  );
};

const disconnectClient = (config: APIConfig, client: Client, observer: Observer<Connection>) =>
  client.disconnect(() => {
    socketDebug('Disconnected from server');
    observer.next({ config, state: ConnectionState.CLOSED, client });
  });

const makeConnection = (config: APIConfig) =>
  new Observable((observer: Observer<Connection>) => {
    const client = over(() => new SockJS(config.url, null, config.sockJSOptions));
    client.debug = debug;

    if (canUseDOM) {
      document.addEventListener('starspause', () => disconnectClient(config, client, observer));
      document.addEventListener('starsresume', () => connectClient(config, client, observer));
    }

    connectClient(config, client, observer);

    return () => {
      disconnectClient(config, client, observer);
      delete subjects[config.url];
    };
  });

export const connect = (config: APIConfig) => {
  if (!subjects[config.url]) {
    subjects[config.url] = makeConnection(config).pipe(
      startWith({ config, state: ConnectionState.DISCONNECTED }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }
  return subjects[config.url];
};
